/* eslint-disable react/display-name */
import React from 'react';
import isNumber from 'lodash/fp/isNumber';
import isString from 'lodash/fp/isString';
import trim from 'lodash/fp/trim';
import _ from 'lodash';
import {
  uniq,
  path,
  isNil,
  isEmpty,
} from 'ramda';
import { getObjectFromArray } from 'pages/Verticals/ResultsPage/ResultsPageUtils';

export function toKebabCase(str) {
  return str && str.toLowerCase().replace(/\s|_/g, '-');
}

export function log(level, sessionObj, toLog) {
  let sId;
  try {
    sId = sessionObj?.sId;
  } catch (e) {
    console.warn(e);
  }
  const currentDatetime = new Date();
  return JSON.stringify({
    timestamp: `${currentDatetime.getFullYear()}-${currentDatetime.getMonth().toString().padStart(2, '0')}-${currentDatetime.getDay().toString().padStart(2, '0')} ${currentDatetime.getHours().toString().padStart(2, '0')}:${currentDatetime.getMinutes().toString().padStart(2, '0')}:${currentDatetime.getSeconds().toString().padStart(2, '0')}.${currentDatetime.getMilliseconds()}`,
    level,
    mdc:
    {
      SID: sId,
    },
    ...toLog,
  });
}

export function toCamelCase(str) {
  const input = str.replace(/-|_/g, ' ').toLowerCase();
  // eslint-disable-next-line
  const convert = (letter, index) => {
    return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
  };
  return input.replace(/(?:^\w|[A-Z]|\b\w)/g, convert).replace(/\s+/g, '');
}

export function getIntegerFromThousandSeparated(currency) {
  if (!currency) return null;
  const parts = `${currency}`.match(/[+-]?\d+(?:\.\d+)?/g);
  if (!parts || !parts.length) return 0;
  const integer = parseInt(parts.join(''), 10);
  if (!integer) return 0;
  return integer;
}

export function getPageRoute(alias, language, vertical, pageRoutes) {
  const productPageRoute = _.template(pageRoutes);
  return productPageRoute({
    language,
    vertical,
    alias,
  });
}

/**
 * @param url
 * @param width
 * @returns string
 */
export function getImageUrl(url, dimensions = {}) {
  const encodedUrl = encodeURI(url);
  if (dimensions.width || dimensions.height) {
    return `${encodedUrl}?width=${dimensions.width * 2 || ''}&height=${dimensions.height * 2 || ''}&auto=webp`;
  }
  return `${encodedUrl}?auto=webp`;
}

export function parseAssetString({
  rawJson,
  property,
  uriEncode,
  dimensions = {},
}) {
  let parsed;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    console.warn(e);
  }
  const matchedValue = parsed?.[property];
  const result = uriEncode ? getImageUrl(matchedValue, dimensions) : matchedValue;
  return result;
}

export function disableBodyScrolling(bool) {
  // https://stackoverflow.com/questions/43563795/bootstrap-modal-background-scroll-on-ios
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (bool) {
    const { scrollY } = window;
    document.body.style.marginTop = `${-scrollY}px`;
    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
  } else {
    const previousScrollY = Math.abs(parseInt(document.body.style.marginTop, 10));
    document.body.style.marginTop = 0;
    document.body.style.overflowY = 'visible';
    document.body.style.position = 'static';
    window.scrollTo(0, previousScrollY);
  }
}

export function getObjectFromStaticTextArray(staticTextArray) {
  return (staticTextArray || [])
    .reduce((a, { key, display }) => Object.assign(a, { [key]: display }), {});
}

export function getLabel({
  staticTextArray = [],
  staticKeysObj = {},
  textKey,
}) {
  return (staticTextArray || [])
    .find(text => text.key === staticKeysObj[textKey]);
}

export function getRuntimeEnvVariables(key) {
  if (typeof window !== 'undefined' && window.env) {
    return key ? window.env[key] : window.env;
  }

  // Return only env starts with 'RUNTIME_'
  const runtimeEnvVars = Object.entries(process.env)
    .reduce((a, [envKey, envValue]) => {
      if (envKey.startsWith('RUNTIME_')) {
        return Object.assign(a, {
          [envKey]: envValue,
        });
      }
      return a;
    }, {});
  return key ? runtimeEnvVars[key] : runtimeEnvVars;
}

export const parseFormattedNumber = (inputString) => {
  if (isNumber(inputString)) {
    return inputString;
  }
  if (!isString(inputString) || trim(inputString) === '') {
    return null;
  }
  const digits = inputString.replace(/[^0-9-]/g, '');
  return digits.length ? parseInt(digits, 10) : digits;
};

export const activtateOptimize = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'optimize.activate' });
};

/**
 * Sanitize component props particularly when defaultProps is not sufficient
 * @param {function} transform receives props as input and output transformed props
 */
export const sanitize = ({ forwardRef } = {}) => transform => Component => (
  /* eslint-disable react/jsx-filename-extension */
  forwardRef
    ? React.forwardRef((props, ref) => <Component ref={ref} {...transform?.(props) ?? props} />)
    : props => <Component {...transform?.(props) ?? props} />
  /* eslint-enable */
);

export const onCtaClick = (ctaButton, callback) => (e) => {
  e.preventDefault();
  const url = ctaButton?.link?.url;

  if (!ctaButton?.show || typeof window === 'undefined') return;

  if (callback) callback(e);
  if (ctaButton?.link?.linkClass === 'INTERNAL') {
    window.location.href = url;
    return;
  }
  const win = window.open(`/external?url=${url}`, '_blank');
  win.focus();
};

export const addStyleString = (str) => {
  if (typeof window === 'undefined') return;
  const node = document.createElement('style');
  node.innerHTML = str;
  document.body.appendChild(node);
};

export function callNTimes({ handler, delay, repetitions }) {
  let n = 0;
  if (typeof window !== 'undefined') {
    const intervalId = window.setInterval(() => {
      if (++n === repetitions || handler() === false) {
        window.clearInterval(intervalId);
      }
    }, delay);
  }
}

export const getObjectFromArrayByKey = (array = [], key) => {
  if (!key) { return array; }
  const output = {};

  if (Array.isArray(array)) {
    array.forEach(({ [key]: keyValue, ...rest }) => {
      output[keyValue] = rest;
    });
  }

  return output;
};

/**
 * Check if URL (not including language and search parameters) is in the urlArray
 */
export const includesURL = (urlArray, URL, language) => {
  let pathname = URL.pathname.split('?')[0];
  if (language) {
    pathname = pathname.replace(`/${language}`, '');
  }

  return !!urlArray?.find(route => route === pathname);
};

export function getAuthQueryVariables(
  {
    contentstackKeys, initailStaticTextKeys = [], initialImages = [], initialComponentConfig = [],
  }
) {
  const { imageAndLink, staticTextKeys, componentConfig } = contentstackKeys;

  return {
    staticTextKeys: uniq([
      ...initailStaticTextKeys,
      ...staticTextKeys.login,
      ...staticTextKeys.accountMenu,
      ...staticTextKeys.routes,
    ]),
    images: [
      ...initialImages,
      ...imageAndLink.login,
    ],
    componentConfig: uniq([
      ...initialComponentConfig,
      ...componentConfig.accountMenu,
    ]),
  };
}

export function getDashboardDesktopMenuBar(staticText) {
  return [
    {
      title: staticText.DASHBOARD_MENUBAR_COMPARE_LINK_TITLE,
      link: staticText.DASHBOARD_MENUBAR_COMPARE_LINK_URL,
    },
    {
      title: staticText.DASHBOARD_MENUBAR_BLOG_LINK_TITLE,
      link: staticText.DASHBOARD_MENUBAR_BLOG_LINK_URL,
    },
    {
      title: staticText.DASHBOARD_MENUBAR_SUPPORT_LINK_TITLE,
      link: staticText.DASHBOARD_MENUBAR_SUPPORT_LINK_URL,
    },
  ];
}

export function getStaticText(data) {
  return getObjectFromArray(path(['pageConfig', 'staticPageText', 'staticText'], data));
}

export function getImages(data) {
  return getObjectFromArrayByKey(path(['images'], data), 'title');
}

export function getComponentConfig(data) {
  const config = getObjectFromArrayByKey(path(['componentConfig'], data), 'title');
  const output = {};

  Object.keys(config).forEach((key) => {
    output[key] = {};

    Object.keys(config[key]).forEach((type) => {
      if (Array.isArray(config[key][type])) {
        config[key][type].forEach((entry) => {
          const { values: { title, ...rest } } = entry;
          output[key] = { ...output[key], [title]: { ...rest } };
        });
      }
    });
  });

  return output;
}

/**
 * Calculate selection offset for currency inputs with 'thousandSeparator' format
 */
export const setSelectionOffset = (prev, next, eventValue, selectionStart) => {
  if (prev > next) {
    if ((eventValue.length === 4
      || (eventValue.length > 7)
      && selectionStart !== 0)
    ) return -1;
  }
  if (prev < next) {
    if (selectionStart === 1) return 0;
    if ((selectionStart <= eventValue.length
      && eventValue.length > 7
      && selectionStart !== 0)
      || eventValue.length === 4
      || (selectionStart >= eventValue.length && eventValue.length >= 4)) return +1;
  }
  return 0;
};

export const isNullish = (val) => (val === null || val === undefined || val === '' || Number.isNaN(val));

export const isNotNullish = (val) => !isNullish(val);

export function formatBytes(bytes, fileSizes, decimals = 2) {
  const base = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor((bytes ? Math.log(bytes) : bytes) / Math.log(base));

  return `${parseFloat((bytes / base ** i).toFixed(dm))} ${fileSizes[i]}`;
}

const sessionStorage = {
  get: (k) => { if (typeof window === 'undefined') { return null; } return window.sessionStorage.getItem(k); },
  set: (k, v) => { if (typeof window === 'undefined') { return null; } return window.sessionStorage.setItem(k, v); },
  remove: (k) => { if (typeof window === 'undefined') { return null; } return window.sessionStorage.removeItem(k); },
  clear: () => { if (typeof window === 'undefined') { return null; } return window.sessionStorage.clear(); },
};

const sessionStorageFromParentWindow = {
  get: (k) => { if (typeof window === 'undefined') { return null; } return window.top.opener?.sessionStorage?.getItem(k); },
  set: (k, v) => { if (typeof window === 'undefined') { return null; } return window.top.opener?.sessionStorage?.setItem(k, v); },
  remove: (k) => { if (typeof window === 'undefined') { return null; } return window.top.opener?.sessionStorage?.removeItem(k); },
  clear: () => { if (typeof window === 'undefined') { return null; } return window.top.opener?.sessionStorage?.clear(); },
};

export const getApplicationFormRetryUrl = () => sessionStorage.get('applicationFormRetryUrl');
export const setApplicationFormRetryUrl = (v) => sessionStorage.set('applicationFormRetryUrl', v);

export const getLoginRedirectUrl = () => sessionStorage.get('loginRedirectUrl');
export const setLoginRedirectUrl = (v) => sessionStorage.set('loginRedirectUrl', v);
export const removeLoginRedirectUrl = () => sessionStorage.remove('loginRedirectUrl');

export const getLoginRedirectUrlFromParentWindow = () => sessionStorageFromParentWindow.get('loginRedirectUrl');
export const setLoginRedirectUrlFromParentWindow = (v) => sessionStorageFromParentWindow.set('loginRedirectUrl', v);
export const removeLoginRedirectUrlFromParentWindow = () => sessionStorageFromParentWindow.remove('loginRedirectUrl');

export const getIsRedirectedFromPopup = () => sessionStorage.get('isRedirectedFromPopup');
export const setIsRedirectedFromPopup = (v) => sessionStorage.set('isRedirectedFromPopup', v);
export const removeIsRedirectedFromPopup = () => sessionStorage.remove('isRedirectedFromPopup');

export const getSignupExitPopupHasBeenShowed = () => sessionStorage.get('signupExitPopupHasBeenShowed');
export const setSignupExitPopupHasBeenShowed = (v) => sessionStorage.set('signupExitPopupHasBeenShowed', v);

export const getMobileExitPopupHasBeenShown = () => sessionStorage.get('mobileExitPopupHasBeenShown');
export const setMobileExitPopupHasBeenShown = (v) => sessionStorage.set('mobileExitPopupHasBeenShown', v);

export const getScaleoClickId = () => sessionStorage.get('SCALEO_CLICK_ID');
export const getScaleoClickType = () => sessionStorage.get('SCALEO_CLICK_TYPE');

export const isShowExitPopup = (userAgent, verticalEnum, exitPopupOrder) => {
  const { showDesktop, showMobile } = exitPopupOrder;
  let { verticals } = exitPopupOrder;
  if (verticals == null) {
    verticals = [verticalEnum];
  }
  if (((showDesktop && !userAgent.isMobile)
    || (showMobile && userAgent.isMobile))
    && verticals.some(v => v === verticalEnum)) {
    return true;
  }
  return false;
};

/**
 * Reduce language keys of an object and return its language value recursively
 * e.g. extractTemplateForLanguage('zh', {en:'foo', zh:'bar'}) = bar;
 */
export const extractTemplateForLanguage = (lang, input) => {
  const availableLangs = ['en', 'zh'];
  if (!availableLangs.includes(lang)) { return input; }

  const run = (obj) => {
    if (typeof obj !== 'object') {
      return obj;
    }
    let toReturn = {};
    Object.keys(obj).forEach((key) => {
      if (availableLangs.includes(key)) {
        if (key === lang) {
          toReturn = obj[key];
        }
      } else {
        toReturn = { ...toReturn, [key]: run(obj[key]) };
      }
    });
    return Array.isArray(obj) ? Object.values(toReturn) : toReturn;
  };

  return run(input);
};

/**
 * Returns CMS dictionary value for a given key if specified, otherwise `defaultValue`.
 *
 * Example: If LOGIN_HEADER is not specified in current CMS stack then,
 * showStaticTextIfSpecified('LOGIN_HEADER', staticText) returns null.
 */
export function showStaticTextIfSpecified(key, staticText, defaultValue = null) {
  return (staticText && staticText[key] !== key) ? staticText[key] : defaultValue;
}

export function getReverseFormattedDob(dob, oldSeparator, newSeparators) {
  if (!dob) {
    return '';
  }
  if (!oldSeparator || !newSeparators) {
    return dob;
  }
  const newDob = dob.split(oldSeparator).reverse().join(newSeparators);
  return newDob;
}

// Use Ramda to instead of Lodash to make result the same
export const isEmptyOrNil = (value) => {
  if (typeof value === 'string') return value.length <= 0;
  return isNil(value) || isEmpty(value) || typeof value !== 'object';
};

export function replacePromoTerms(input, text, link) {
  if (!input) return input;
  return input.replace('$promoTerms', `<a target="_blank" href="${link}" data-selector="promoTermsAndConditionsLink">${text}</a>`);
}
